import * as React from 'react';
import { defaultMemoize } from 'reselect';

import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs,
} from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { Subspace, TabbedSpace } from '../../app/spaces/TabbedSpace';
import { PublishedConnectionSubspaceName } from '../../app/state/SpaceInfo';
import { Formatters, Messages } from '../../codegen/Localize';
import { IPublishedConnectionDetailInfo, IPublishedConnectionInfo } from '../../ts/models/IPublishedConnectionInfo';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { IPublishedConnectionActions } from '../../ts/ServerApi/types/IPublishedConnectionActions';
import { PublishedConnectionOverviewSubplace } from './PublishedConnectionOverviewSubplace';
import { PublishedConnectionSpaceHeader } from './PublishedConnectionSpaceHeader';

export type PublishedConnectionSpaceStateProps = {
  readonly urls: SpacesSiteUrls;
};

export type PublishedConnectionSpaceDispatchProps = {
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
};

export interface PublishedConnectionSpaceOwnProps {
  readonly subplaceName: string;
  readonly publishedConnectionAllowedActions: IPublishedConnectionActions;
  readonly publishedConnectionDetailInfo: IPublishedConnectionDetailInfo;
  readonly publishedConnectionInfo: IPublishedConnectionInfo;
  readonly projectHierarchy: INamedItemInfo[];
}

export type PublishedConnectionSpaceProps =
  PublishedConnectionSpaceStateProps
  & PublishedConnectionSpaceDispatchProps
  & PublishedConnectionSpaceOwnProps;

function overviewSubspace(publishedConnectionDetailInfo: IPublishedConnectionDetailInfo): Subspace {
  const tablesCount = publishedConnectionDetailInfo.tables ? publishedConnectionDetailInfo.tables.length : 0;
  return {
    name: 'overview',
    tabLabel: Messages.publishedConnectionTablesTabHeader({COUNT: Formatters.formatCount(tablesCount)}),
    renderContent: () => <PublishedConnectionOverviewSubplace
      publishedConnectionDetailInfo={publishedConnectionDetailInfo}/>,
    shouldRenderTabContentCount: false,
  };
}

function getPublishedConnectionDetailWithUsage(
  publishedConnectionDetailInfo: IPublishedConnectionDetailInfo,
  publishedConnectionInfo: IPublishedConnectionInfo,
) {
  return {
    usageInfo: publishedConnectionInfo.usageInfo,
    usageInfoMaxValues: publishedConnectionInfo.usageInfoMaxValues,
    ...publishedConnectionDetailInfo,
  };
}

export class PublishedConnectionSpace extends React.PureComponent<PublishedConnectionSpaceProps> {
  private memoizedPublishedConnectionDetailWithUsage = defaultMemoize(getPublishedConnectionDetailWithUsage);
  public urlFromSubspaceName = (subspaceName?: PublishedConnectionSubspaceName) => {
    switch (subspaceName) {
      case 'overview':
        return this.props.urls.publishedConnectionOverviewUrl(this.props.publishedConnectionDetailInfo.id);
    }
    return this.props.urls.publishedConnectionUrl(this.props.publishedConnectionDetailInfo.id);
  };

  private updateBreadcrumbs() {
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(this.props.projectHierarchy);
    populateContentBreadcrumbs([...projectHierarchyWithType, this.props.publishedConnectionInfo], this.props.urls);
  }

  public componentDidMount() {
    this.updateBreadcrumbs();
  }

  public componentDidUpdate(prevProps: PublishedConnectionSpaceProps) {
    if (this.props.publishedConnectionDetailInfo !== prevProps.publishedConnectionDetailInfo) {
      this.updateBreadcrumbs();
    }
  }

  public render() {
    const subspaces = [overviewSubspace(this.props.publishedConnectionDetailInfo)];

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subplaceName as PublishedConnectionSubspaceName}
        urlForSubspace={this.urlFromSubspaceName}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'publishedConnection'}
        spaceItemName={this.props.publishedConnectionDetailInfo.name}
      />
    );
  }

  private mastheadElement(): React.ReactNode {
    const publishedConnectionDetailWithUsage = this.memoizedPublishedConnectionDetailWithUsage(
      this.props.publishedConnectionDetailInfo,
      this.props.publishedConnectionInfo,
    );

    return (
      <PublishedConnectionSpaceHeader
        item={publishedConnectionDetailWithUsage}
        availableActions={this.props.publishedConnectionAllowedActions}
        addToFavorites={this.props.addToFavorites}
        removeFromFavorites={this.props.removeFromFavorites}
        urls={this.props.urls}
      />
    );
  }
}
